import React, { ImgHTMLAttributes, useRef, useState } from "react";
import styled from "@emotion/styled";

export interface CoverImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  onFit?: (img: HTMLImageElement) => void;
}

const StyledImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;

  &[loading="lazy"] {
    height: 100%;
  }
`;

export const CoverImage = ({ onFit, ...props }: CoverImageProps) => {
  const [dimensions, setDimensions] = useState<[number, number] | undefined>(
    undefined
  );
  const imgElem = useRef<HTMLImageElement>(null);
  const style = { ...props.style };
  const attrs = {
    alt: props.alt ?? "",
    style,
    ...props,
    ...(dimensions && {
      width: dimensions[0],
      height: dimensions[1],
    }),
  };

  // After a cover image is loaded, take note of the DOM dimensions and
  // set the height and width to those dimensions. This is helpful when the
  // intrinsic height and width of the image is not known (ie: most of the time).
  // To guard against our image assuming a height of zero when a new src is
  // presented (eg: a new scent is selected so the PDP carousel images are swapped)
  // we update the height and width on the image using JS.
  const handleOnLoad: React.ReactEventHandler<HTMLImageElement> = (e) => {
    if (onFit && imgElem.current) {
      onFit(imgElem.current);
    }

    setDimensions([
      e.currentTarget.naturalWidth,
      e.currentTarget.naturalHeight,
    ]);
  };

  return <StyledImg onLoad={handleOnLoad} ref={imgElem} {...attrs} />;
};
